import React, { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import UIContainer from 'components/ui/UIContainer';
import PageIntro from 'components/PageIntro/PageIntro';

type SectionKey = 'teachers' | 'clubs' | 'sets' | 'board' | 'ideas';

interface SidebarItem {
  key: SectionKey;
  Icon: typeof SchoolOutlinedIcon;
  label: string;
  description: string;
  eta: string;
}

const SIDEBAR_ITEMS: SidebarItem[] = [
  {
    key: 'teachers',
    Icon: SchoolOutlinedIcon,
    label: 'Викладачі',
    description:
      'Каталог викладачів англійської — їхні профілі, спеціалізації, ставки й слоти для пробного заняття. Можна бронювати індивідуальні уроки прямо з платформи.',
    eta: 'Скоро',
  },
  {
    key: 'clubs',
    Icon: ForumOutlinedIcon,
    label: 'Розмовні клуби',
    description:
      'Регулярні онлайн-зустрічі для практики мови — від клубів-початківців до advanced-обговорень. Записуйся на найближчу сесію або створюй власний клуб.',
    eta: 'Скоро',
  },
  {
    key: 'sets',
    Icon: AutoStoriesOutlinedIcon,
    label: 'Набори спільноти',
    description:
      'Публічні набори слів і виразів від інших користувачів: підбірки з аналізів «В дії», тематичні колекції та авторські словники. Знаходь, копіюй до себе й вивчай.',
    eta: 'Літо 2026',
  },
  {
    key: 'board',
    Icon: CampaignOutlinedIcon,
    label: 'Дошка оголошень',
    description:
      'Місце, де користувачі ділять корисні матеріали, шукають мовних партнерів, анонсують події та обговорюють виклики. Стрічка коротких повідомлень від спільноти.',
    eta: 'Осінь 2026',
  },
  {
    key: 'ideas',
    Icon: LightbulbOutlinedIcon,
    label: 'Ідеї та пропозиції',
    description:
      'Пропонуй нові функції, голосуй за ідеї інших користувачів і стеж за тим, що з’явиться в застосунку далі. Твій фідбек напряму впливає на розвиток продукту.',
    eta: 'Скоро',
  },
];

const CommunityPage: React.FC = () => {
  const [active, setActive] = useState<SectionKey>('teachers');
  const activeItem =
    SIDEBAR_ITEMS.find((it) => it.key === active) || SIDEBAR_ITEMS[0];

  return (
    <>
      <UIContainer>
        <BreadCrumbs />
      </UIContainer>
      <UIContainer>
        <Box pb={5} pt={2}>
          <Stack spacing={2.75}>
            <PageIntro
              surface
              eyebrow={
                <Typography
                  sx={{
                    color: 'primary.main',
                    fontSize: 12,
                    fontWeight: 800,
                    letterSpacing: '0.09em',
                    textTransform: 'uppercase',
                  }}
                >
                  {'Спільнота'}
                </Typography>
              }
              title={'Вчись разом з іншими'}
              description={
                'Знаходь публічні набори слів і виразів, які створюють інші користувачі, домовляйся з викладачами, приєднуйся до розмовних клубів і ділися знахідками на дошці оголошень.'
              }
            />

            <Box
              sx={{
                display: 'grid',
                gap: { xs: 2, md: 2.5 },
                gridTemplateColumns: { xs: '1fr', md: '240px 1fr' },
                alignItems: 'flex-start',
              }}
            >
              {/* Sidebar */}
              <Stack
                spacing={0.5}
                sx={{
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'primary.tableBorder',
                  borderRadius: 2,
                  p: 1,
                  position: { md: 'sticky' },
                  top: { md: 16 },
                }}
              >
                {SIDEBAR_ITEMS.map((item) => {
                  const on = item.key === active;
                  return (
                    <Box
                      key={item.key}
                      component={'button'}
                      onClick={() => setActive(item.key)}
                      sx={{
                        alignItems: 'center',
                        bgcolor: (theme) =>
                          on
                            ? theme.palette.mode === 'light'
                              ? 'rgba(59, 44, 147, 0.10)'
                              : 'rgba(155, 124, 255, 0.18)'
                            : 'transparent',
                        border: 'none',
                        borderRadius: 1.5,
                        color: on ? 'primary.main' : 'text.primary',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        fontFamily: 'inherit',
                        fontSize: 14.5,
                        fontWeight: on ? 700 : 600,
                        gap: 1.25,
                        justifyContent: 'flex-start',
                        px: 1.25,
                        py: 1,
                        textAlign: 'left',
                        transition: 'background-color 120ms ease',
                        width: '100%',
                        '&:hover': {
                          bgcolor: (theme) =>
                            on
                              ? theme.palette.mode === 'light'
                                ? 'rgba(59, 44, 147, 0.14)'
                                : 'rgba(155, 124, 255, 0.22)'
                              : 'background.default',
                        },
                      }}
                    >
                      <item.Icon
                        sx={{
                          color: on ? 'primary.main' : 'text.secondary',
                          fontSize: 20,
                        }}
                      />
                      {item.label}
                    </Box>
                  );
                })}
              </Stack>

              {/* Content area */}
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'primary.tableBorder',
                  borderRadius: 2,
                  p: { xs: 2.5, md: '28px 30px' },
                }}
              >
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  spacing={1.5}
                  sx={{ mb: 1.5 }}
                >
                  <Box
                    sx={{
                      alignItems: 'center',
                      bgcolor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(59, 44, 147, 0.08)'
                          : 'rgba(155, 124, 255, 0.15)',
                      borderRadius: 1.5,
                      color: 'primary.main',
                      display: 'inline-flex',
                      flex: 'none',
                      height: 44,
                      justifyContent: 'center',
                      width: 44,
                    }}
                  >
                    <activeItem.Icon sx={{ fontSize: 24 }} />
                  </Box>
                  <Typography sx={{ flex: 1, fontSize: 22, fontWeight: 800 }}>
                    {activeItem.label}
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(59, 44, 147, 0.10)'
                          : 'rgba(155, 124, 255, 0.18)',
                      borderRadius: 999,
                      color: 'primary.main',
                      fontSize: 11,
                      fontWeight: 800,
                      letterSpacing: '0.06em',
                      px: 1.25,
                      py: 0.5,
                      textTransform: 'uppercase',
                    }}
                  >
                    {activeItem.eta}
                  </Box>
                </Stack>
                <Typography
                  sx={{
                    color: 'text.secondary',
                    fontSize: 15,
                    lineHeight: 1.55,
                    maxWidth: 640,
                  }}
                >
                  {activeItem.description}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>
      </UIContainer>
    </>
  );
};

export default CommunityPage;
